/**
 * Path resolution and value matching shared by the frontend and backend check
 * filter matchers, so both evaluate a {@link CheckFilter} rule identically.
 */

/** Entity path prefix under which annotation filters are stored. */
export const ANNOTATION_PATH_PREFIX = 'metadata.annotations';

/** Rule value meaning "the path resolves to something", regardless of value. */
export const FILTER_VALUE_EXISTS = '*';

/**
 * Filter path for an annotation key. Bracket notation is required because
 * annotation keys contain dots (`backstage.io/techdocs-ref`), which plain dot
 * paths cannot represent unambiguously.
 */
export const buildAnnotationPath = (key: string): string =>
  `${ANNOTATION_PATH_PREFIX}[${JSON.stringify(key)}]`;

const unquote = (value: string): string => {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    try {
      return JSON.parse(value) as string;
    } catch {
      return value.slice(1, -1);
    }
  }
  if (value.length >= 2 && value.startsWith("'") && value.endsWith("'")) {
    return value.slice(1, -1);
  }
  return value;
};

/** The annotation key a filter path targets, or undefined for other paths. */
export const parseAnnotationPath = (path: string): string | undefined => {
  const prefix = `${ANNOTATION_PATH_PREFIX}[`;
  const trimmed = path.trim();
  if (!trimmed.startsWith(prefix) || !trimmed.endsWith(']')) {
    return undefined;
  }

  return unquote(trimmed.slice(prefix.length, -1)) || undefined;
};

const SEGMENT_DELIMITERS = new Set(['.', '[', ']']);

/** Index just past the plain (unbracketed) segment starting at `from`. */
const endOfPlainSegment = (path: string, from: number): number => {
  let end = from;
  while (end < path.length && !SEGMENT_DELIMITERS.has(path[end])) {
    end += 1;
  }
  return end;
};

/**
 * Splits `metadata.annotations["a.b/c"]` into its literal property names.
 * Scanned rather than matched with a regex: filter paths are authored input,
 * and a backtracking pattern over them is a ReDoS risk.
 */
export const toPathSegments = (path: string): string[] => {
  const segments: string[] = [];
  let index = 0;

  while (index < path.length) {
    const char = path[index];

    if (char === '[') {
      const end = path.indexOf(']', index + 1);
      if (end === -1) {
        break;
      }
      segments.push(unquote(path.slice(index + 1, end)));
      index = end + 1;
    } else if (SEGMENT_DELIMITERS.has(char)) {
      index += 1;
    } else {
      const end = endOfPlainSegment(path, index);
      segments.push(path.slice(index, end));
      index = end;
    }
  }

  return segments.filter(Boolean);
};

/** Resolves a dot and/or bracket path against an entity-like object. */
export const getValueByPath = (input: unknown, path: string): unknown => {
  if (!input || typeof input !== 'object') {
    return undefined;
  }

  let current: unknown = input;
  for (const segment of toPathSegments(path)) {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
};

/** Compares one resolved value against one expected rule value. */
export const filterValueMatches = (
  actual: unknown,
  expected: unknown,
): boolean => {
  if (expected === FILTER_VALUE_EXISTS) {
    return actual !== undefined && actual !== null && actual !== '';
  }

  if (typeof actual === 'string' && typeof expected === 'string') {
    return actual.toLowerCase() === expected.toLowerCase();
  }

  return actual === expected;
};
